import React, { useEffect, useState } from 'react';
import { Building2, Plus, Edit2, Trash2, X, AlertCircle, Search, Phone, MapPin, Mail, User, CreditCard } from 'lucide-react';
import { getSupplier, createSupplier, updateSupplier, deleteSupplier } from '../../api';
import { useAuth } from '../../AuthContext';

const emptyForm = { nama: '', telepon: '', alamat: '', email: '', pic: '', catatan: '', nama_bank: '', no_rekening: '', nama_rekening: '' };

export default function Supplier() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadData = () => {
    setLoading(true);
    getSupplier()
      .then(res => setData(res.data || []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadData(); }, []);

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') loadData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = data.filter(s =>
    !search ||
    s.nama?.toLowerCase().includes(search.toLowerCase()) ||
    s.kode?.toLowerCase().includes(search.toLowerCase()) ||
    s.telepon?.includes(search) ||
    s.no_rekening?.includes(search) ||
    s.nama_bank?.toLowerCase().includes(search.toLowerCase()) ||
    s.nama_rekening?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingItem(null); setForm({ ...emptyForm, kode: '' }); setError(''); setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      kode: item.kode || '', nama: item.nama || '', telepon: item.telepon || '',
      alamat: item.alamat || '', email: item.email || '', pic: item.pic || '', catatan: item.catatan || '',
      nama_bank: item.nama_bank || '',
      no_rekening: item.no_rekening || '', nama_rekening: item.nama_rekening || '',
    });
    setError(''); setShowModal(true);
  };

  const getNewKode = () => {
    // Find max SPL number
    let max = 0;
    data.forEach(s => {
      const m = s.kode?.match(/^SPL-(\d+)$/i);
      if (m) {
        const n = parseInt(m[1], 10);
        if (n > max) max = n;
      }
    });
    return `SPL-${String(max + 1).padStart(3, '0')}`;
  };

  const handleSave = async () => {
    setError('');
    if (!form.nama) { setError('Nama supplier harus diisi'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await updateSupplier(editingItem.id, form);
      } else {
        await createSupplier(form);
      }
      setShowModal(false); loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin hapus supplier "${item.nama}"?`)) return;
    try { await deleteSupplier(item.id); loadData(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2><Building2 size={20} style={{ marginRight: 8 }} />Master Supplier</h2>
            <p className="page-desc">{data.length} supplier terdaftar</p>
          </div>
          {hasMenuAction('supplier', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} /><span>Tambah Supplier</span>
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={18} />
          <input type="text" placeholder="Cari supplier..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Kode</th><th>Nama Supplier</th><th>Telepon</th><th>Email</th><th>PIC</th>
                <th>Nama Bank</th><th>No. Rekening</th><th>Nama Rekening</th><th style={{ width: 100 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id}>
                  <td><span className="badge badge-info">{s.kode || '-'}</span></td>
                  <td><strong>{s.nama}</strong></td>
                  <td><span style={{ fontSize: '0.82rem' }}>{s.telepon || '-'}</span></td>
                  <td><span style={{ fontSize: '0.82rem' }}>{s.email || '-'}</span></td>
                  <td><span style={{ fontSize: '0.82rem' }}>{s.pic || '-'}</span></td>
                  <td>
                    {s.nama_bank ? (
                      <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{s.nama_bank}</span>
                    ) : (
                      <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>-</span>
                    )}
                  </td>
                  <td>
                    {s.no_rekening ? (
                      <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600 }}>{s.no_rekening}</span>
                    ) : (
                      <span style={{ fontSize: '0.78rem', color: '#d97706' }}>belum diisi</span>
                    )}
                  </td>
                  <td><span style={{ fontSize: '0.82rem' }}>{s.nama_rekening || '-'}</span></td>
                  <td>
                    <div className="action-btns">
                      {hasMenuAction('supplier', 'edit') && (
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(s)}><Edit2 size={14} /></button>
                      )}
                      {hasMenuAction('supplier', 'delete') && (
                        <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(s)}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} className="text-center text-muted" style={{ padding: 40 }}>Belum ada supplier</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingItem ? 'Edit Supplier' : 'Tambah Supplier Baru'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div style={{ display: 'grid', gridTemplateColumns: editingItem ? '1fr 2fr' : '2fr', gap: 10 }}>
                {editingItem ? (
                  <div className="form-group"><label>Kode</label><input type="text" value={form.kode} onChange={e => setForm({...form, kode: e.target.value})} placeholder="SPL-001" /></div>
                ) : (
                  <div className="form-group">
                    <label>Kode</label>
                    <div className="form-hint" style={{ padding: '10px 12px', background: 'rgba(99,102,241,0.08)', borderRadius: 6, fontSize: '0.85rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600 }}>{getNewKode()}</span>
                      <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>— otomatis</span>
                    </div>
                  </div>
                )}
                <div className="form-group"><label>Nama Supplier *</label><input type="text" value={form.nama} onChange={e => setForm({...form, nama: e.target.value})} placeholder="PT. Supplier Jaya" /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div className="form-group"><label><Phone size={13} style={{ marginRight: 4 }} />Telepon</label><input type="text" value={form.telepon} onChange={e => setForm({...form, telepon: e.target.value})} placeholder="021-xxxx" /></div>
                <div className="form-group"><label><Mail size={13} style={{ marginRight: 4 }} />Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="supplier@email.com" /></div>
              </div>
              <div className="form-group" style={{ marginTop: 10 }}>
                <label><MapPin size={13} style={{ marginRight: 4 }} />Alamat</label>
                <textarea rows={2} value={form.alamat} onChange={e => setForm({...form, alamat: e.target.value})} placeholder="Alamat lengkap supplier" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div className="form-group"><label><User size={13} style={{ marginRight: 4 }} />PIC</label><input type="text" value={form.pic} onChange={e => setForm({...form, pic: e.target.value})} placeholder="Nama kontak person" /></div>
                <div className="form-group"><label>Catatan</label><input type="text" value={form.catatan} onChange={e => setForm({...form, catatan: e.target.value})} placeholder="Catatan (opsional)" /></div>
              </div>

              {/* Rekening bank — dipakai saat transfer pembayaran hutang */}
              <div style={{
                marginTop: 12, padding: '10px 12px 4px', borderRadius: 8,
                background: 'rgba(79,110,247,0.06)', border: '1px solid rgba(79,110,247,0.2)',
              }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#3b54c9', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CreditCard size={14} /> Rekening Bank (untuk transfer pembayaran)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div className="form-group"><label>Nama Bank</label>
                    <input type="text" value={form.nama_bank} onChange={e => setForm({...form, nama_bank: e.target.value})} placeholder="Mis. BCA" />
                  </div>
                  <div className="form-group"><label>No. Rekening</label>
                    <input type="text" value={form.no_rekening} onChange={e => setForm({...form, no_rekening: e.target.value})} placeholder="Mis. 1234567890" />
                  </div>
                  <div className="form-group"><label>Nama Rekening</label>
                    <input type="text" value={form.nama_rekening} onChange={e => setForm({...form, nama_rekening: e.target.value})} placeholder="Nama pemilik rekening" />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
